import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaTags } from 'react-icons/fa';
import * as d3 from 'd3';
import { Alert } from '@mui/material';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';

import Description from './components/Description';
import Tooltip from './components/Tooltip';
import ZoomControls from './components/ZoomControls';
import HomeButton from './components/HomeButton';
import SearchBar from './components/SearchBar';
import Breadcrumb from './components/Breadcrumb';
import RadarChart from './components/RadarChart';
import TreeChart from './components/TreeChart';
//import ModeSelector from './components/ModeSelector';
import NavigationPanel from './components/NavigationPanel';

import useChartData from './hooks/useChartData';
import useZoomPan from './hooks/useZoomPan';
import useTooltip from './hooks/useTooltip';
import useSearch from './hooks/useSearch';
import { getParentSubcategoryData, findDescendantById } from './utils/dataHelpers';
import { useRadarData } from '../../hooks/useRadarData';
import { APPROVAL_STAGE_COLORS, APPROVAL_STAGES } from '../../constants/radar';
import { radarApi, projectsApi, tagsApi } from '../../services/api';

import { useAlert } from '../../contexts/AlertContext';
import { transformNode } from '../../utils/dataTransformers';

import { TREE_CHART_MODES } from '../../constants/radar';

import {
  Container,
  Content,
  ChartPanel,
} from './styles';

import Header from './components/Header';
import { SEGMENT_TYPE } from '../../constants/radar';
import TagSelectorModal from '../common/TagSelectorModal';
import SelectionPanel from '../common/SelectionPanel';
import { SEGMENT_PROCESS_APPLIES_TO, SEGMENT_VISIBILITY_STATUS } from '../../constants/segmentProcess';

const TrendRadarChart = ({ 
  configMode: initialConfigMode, 
  chartType: initialChartType,
  hideHeader = false,
  hideNavigation = false,
  hideDescription = false,
  onNodeSelect = null,
  embedded = false
}) => {
  const navigate = useNavigate();
  const { email, projectId } = useParams();
  const { data: initialRadarData, loading, error, refresh } = useRadarData();
  const [radarData, setRadarData] = useState(null);
  const [chartType, setChartType] = React.useState(initialChartType || TREE_CHART_MODES.TREE);
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagsError, setTagsError] = useState(null);

  const [projectData, setProjectData] = useState(null);

  const [configMode, setConfigMode] = useState(initialConfigMode || TREE_CHART_MODES.TREE);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [expandedPath, setExpandedPath] = useState([]);
  const [initialCheckedLeafs, setInitialCheckedLeafs] = useState(new Set());
  const [isTreeExpanded, setIsTreeExpanded] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState(false);
  const [isNavigationExpanded, setIsNavigationExpanded] = useState(!hideNavigation);
  const [isPanelsVisible, setIsPanelsVisible] = useState(!embedded);

  const { showAlert, startLoading, stopLoading } = useAlert();

  // Helper to check if a segment's visibility is disabled for a given mode
  const isSegmentVisibilityDisabled = useCallback((node, appliesTo) => {
    if (!node?.segmentProcesses) return false;
    const process = node.segmentProcesses.find(p => p.appliesTo === appliesTo);
    return process?.segmentVisibility === SEGMENT_VISIBILITY_STATUS.DISABLED;
  }, []);

  // Filter data by segment process visibility for a given mode (e.g., PROJECT, EMPLOYEE)
  const filterDataByVisibility = useCallback((data, appliesTo) => {
    if (!appliesTo) return data;

    const filterNodes = (node) => {
      if (!node) return null;

      // Check if this INTERNAL node has visibility disabled for the given mode
      if (node.segmentType === SEGMENT_TYPE.INTERNAL) {
        if (isSegmentVisibilityDisabled(node, appliesTo)) {
          return null; // Hide this entire branch
        }
      }

      // For leaf nodes, just return as-is (visibility is controlled at the parent level)
      if (node.segmentType === SEGMENT_TYPE.LEAF) {
        return node;
      }

      // Filter subcategories recursively
      const filteredSubcategories = (node.subcategories || [])
        .map(child => filterNodes(child))
        .filter(Boolean);

      // Return node with filtered subcategories
      return { ...node, subcategories: filteredSubcategories };
    };

    return filterNodes(data);
  }, [isSegmentVisibilityDisabled]);

  // Add filterDataByTags function
  const filterDataByTags = useCallback((data, selectedTags) => {
    if (!selectedTags.length) return data;

    const filterNodes = (node) => {
      if (!node) return null;

      // Check if it's a leaf node by segmentType instead of subcategories
      if (node.segmentType === SEGMENT_TYPE.LEAF) {
        const hasSelectedTag = selectedTags.some(selectedTag => 
          node.tags?.some(nodeTag => 
            nodeTag.id === selectedTag.id || 
            nodeTag.parentTagId === selectedTag.id
          )
        );

        return hasSelectedTag ? node : null;
      }

      // Filter subcategories
      const filteredSubcategories = (node.subcategories || [])
        .map(child => filterNodes(child))
        .filter(Boolean);

      // Only include non-leaf nodes if they have filtered subcategories
      return filteredSubcategories.length > 0
        ? { ...node, subcategories: filteredSubcategories }
        : null;
    };

    // Return only filtered results, not the original data if no matches
    return filterNodes(data);
  }, []);

  // Inject custom segments into the tree under "Custom Technologies" category (for PROJECT mode)
  const injectCustomSegments = useCallback((treeData) => {
    // Find all custom segments (where leafSegment is null but customSegment exists)
    const customSegments = projectData?.leafSegmentProjects?.filter(
      s => s.leafSegment == null && s.customSegment != null
    ) || [];

    if (customSegments.length === 0) {
      return treeData;
    }

    // Create "Custom Technologies" category node (first-level INTERNAL node)
    const customTechNode = {
      id: 'custom-technologies',
      name: 'Custom Technologies',
      segmentType: SEGMENT_TYPE.INTERNAL,
      internalSegmentId: 'custom-technologies',
      color: '#9C27B0',  // Purple color for custom category
      subcategories: [],
      segmentProcesses: []
    };

    // Create leaf nodes for each custom segment, sorted by name
    const sortedCustomSegments = [...customSegments].sort((a, b) =>
      (a.customSegment.name || '').localeCompare(b.customSegment.name || '')
    );

    sortedCustomSegments.forEach((segment) => {
      const customLeafNode = {
        id: segment.customSegment.id,
        name: segment.customSegment.name,
        segmentType: SEGMENT_TYPE.LEAF,
        leafSegmentId: segment.customSegment.id,
        isCustomSegment: true,  // Flag to identify custom segments
        tags: [],
        approvalStage: APPROVAL_STAGES.HOLD,
        projects: [],
        subcategories: []
      };

      customTechNode.subcategories.push(customLeafNode);
    });

    // Inject as first-level category in original tree
    return {
      ...treeData,
      subcategories: [...(treeData.subcategories || []), customTechNode]
    };
  }, [projectData]);

  // Base filtered data - only depends on visibility and tags, NOT on checkbox state
  const baseFilteredData = useMemo(() => {
    let filteredData = radarData;
    if (!filteredData) return null;

    // Apply visibility filter based on configMode
    if (configMode === TREE_CHART_MODES.PROJECT || configMode === TREE_CHART_MODES.PROJECT_READ_ONLY) {
      filteredData = filterDataByVisibility(filteredData, SEGMENT_PROCESS_APPLIES_TO.PROJECT);
    } else if (configMode === TREE_CHART_MODES.EMPLOYEE || configMode === TREE_CHART_MODES.EMPLOYEE_READ_ONLY) {
      filteredData = filterDataByVisibility(filteredData, SEGMENT_PROCESS_APPLIES_TO.EMPLOYEE);
    }

    if (!filteredData) return null;

    // Inject custom segments for PROJECT mode
    if (configMode === TREE_CHART_MODES.PROJECT) {
      filteredData = injectCustomSegments(filteredData);
    }

    // Apply tag filter
    const tagFiltered = filterDataByTags(filteredData, selectedTags);

    // If no nodes match tag criteria, return empty root node
    return tagFiltered || {
      ...filteredData,
      subcategories: []
    };
  }, [radarData, selectedTags, filterDataByTags, configMode, filterDataByVisibility, injectCustomSegments]);

  // Final filtered data - applies selection filter only when showOnlySelected is true
  const filteredData = useMemo(() => {
    if (!baseFilteredData) return null;

    // Only apply selection filter when showOnlySelected is enabled
    if (!showOnlySelected) {
      return baseFilteredData;
    }

    const filterBySelection = (node) => {
      if (!node) return null;

      if (node.segmentType === SEGMENT_TYPE.LEAF) {
        return initialCheckedLeafs.has(node.leafSegmentId) ? node : null;
      }

      const filteredSubcategories = (node.subcategories || [])
        .map(filterBySelection)
        .filter(Boolean);

      return filteredSubcategories.length > 0
        ? { ...node, subcategories: filteredSubcategories }
        : null;
    };

    const selectionFiltered = filterBySelection(baseFilteredData);
    // If no nodes match selection criteria, return empty root node
    return selectionFiltered || {
      ...baseFilteredData,
      subcategories: []
    };
  }, [baseFilteredData, showOnlySelected, initialCheckedLeafs]);

  const {
    tooltipData,
    tooltipPosition,
    hoveredItem,
    handleHover,
    handleMouseOut
  } = useTooltip();

  //TODO something is wrong here with chartType
  const {
    selectedSubcategory,
    descriptionData,
    handleSelection,
    handleBack,
    getCurrentData
  } = useChartData(radarData || {}, chartType);

  const {
    treeZoomFactor,
    radarZoomFactor,
    panPosition,
    isDragging,
    handleZoomIn,
    handleZoomOut,
    handleResetPosition,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    getZoomFactor,
  } = useZoomPan();

  const {
    searchTerm,
    searchResults,
    showResults,
    isGlobalSearch,
    handleSearch,
    handleResultSelect,
    setIsGlobalSearch,
    showNoResultsMessage,
    clearSearch
  } = useSearch(filteredData || {}, selectedSubcategory);

  // Add this useEffect to fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectsData = await projectsApi.getAllProjects();
        setProjects(projectsData);
      } catch (error) {
        showAlert('Failed to fetch projects', 'error', error);
      }
    };
  
        fetchProjects();
    }, []);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (selectedProject) {
        try {
            const data = await projectsApi.getProjectById(selectedProject);
            setProjectData(data);
          } catch (error) {
          console.error('Error fetching project data:', error);
        }
      }
    };
  
    fetchProjectData();
  }, [selectedProject]);

  useEffect(() => {
    if (initialRadarData) {
      setRadarData(initialRadarData);
    }
  }, [initialRadarData]);

  useEffect(() => {
    if (projectId) {
      // Load project data using the projectId
      // Update your existing data loading logic here
      setSelectedProject(projectId);
      setConfigMode(TREE_CHART_MODES.PROJECT);
    }
  }, [projectId]);

  useEffect(() => {
    const fetchTags = async () => {
      setTagsLoading(true);
      try {
        const tagsData = await tagsApi.getTagHierarchy();
        setTags(tagsData);
        setTagsLoading(false);
      } catch (error) {
        console.error('Failed to load tags:', error);
        setTagsError(error.message);
        setTagsLoading(false);
      }
    };

    fetchTags();
  }, []);

  useEffect(() => {
    if (!selectedProject) {
      setInitialCheckedLeafs(new Set());
      return;
    }

    if (!projectData) {
      return;
    }

    if (projectData.leafSegmentProjects) {
      // Include both regular leaf segments and custom segments
      const regularLeafs = projectData.leafSegmentProjects
        .filter(project => project.leafSegment != null)
        .map(project => project.leafSegment.id);

      const customLeafs = projectData.leafSegmentProjects
        .filter(project => project.leafSegment == null && project.customSegment != null)
        .map(project => project.customSegment.id);

      const checkedIds = new Set([...regularLeafs, ...customLeafs]);
      setInitialCheckedLeafs(checkedIds);
    } else {
      setInitialCheckedLeafs(new Set());
    }
  }, [selectedProject, projectData]);

  const updateTreeData = (data, nodeId, updateFn) => {
    if (data.id === nodeId) {
      return updateFn(data);
    }
    if (data.subcategories) {
      return {
        ...data,
        subcategories: data.subcategories.map(child => 
          updateTreeData(child, nodeId, updateFn)
        )
      };
    }
    return data;
  };

  const updateParentNode = (data, parentId, updateFn) => {
    if (data.id === parentId) {
      return updateFn(data);
    }
    if (data.subcategories) {
      return {
        ...data,
        subcategories: data.subcategories.map(child => 
          updateParentNode(child, parentId, updateFn)
        )
      };
    }
    return data;
  };

  const handleLeafToggle = async (leafId, isChecked, options = {}) => {
    const { isCustomSegment = false } = options;
    try {
      startLoading();

      if (isChecked) {
        await projectsApi.addLeafToProject(selectedProject, leafId);
        showAlert('Leaf added to project successfully', 'success');
      } else {
        try {
          await projectsApi.removeLeafFromProject(selectedProject, leafId, { isCustomSegment });
          showAlert('Leaf removed from project successfully', 'success');
        } catch (error) {
          // Get the original error data from the API
          const errorData = error.data || (error.cause?.data);
          if (errorData?.dashboards) {
            const dashboardNames = errorData.dashboards.map(d => d.name).join(', ');
            showAlert(`Cannot remove leaf as it is assigned to dashboards: ${dashboardNames}`, 'error', error);
          } else {
            showAlert(errorData?.error || error.message || 'Failed to remove leaf from project', 'error', error);
          }
          throw error; // Propagate error to trigger checkbox revert
        }
      }
      
      // Refresh project data after successful update
      const updatedProjectData = await projectsApi.getProjectById(selectedProject);
      setProjectData(updatedProjectData);
      
    } catch (error) {
      console.error('Error updating project leaf:', error);
      // Don't show another error message since we've already shown one
      throw error; // Propagate error to trigger checkbox revert
    } finally {
      stopLoading();
    }
  };

  const isLeafAssigned = useCallback((leafId) => {
    return projectData?.leafSegmentProjects?.some(
      (leafProject) => leafProject.leafSegment?.segmentId === leafId || leafProject.customSegment?.id === leafId
    ) || false;
  }, [projectData]);

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      const isSelected = prev.some(t => t.id === tag.id);
      if (isSelected) {
        return prev.filter(t => t.id !== tag.id);
      }
      return [...prev, tag];
    });
  };

  const handleTogglePanels = () => {
    setIsPanelsVisible(!isPanelsVisible);
  };

  // Handle custom segment creation for projects (segments not in the radar)
  const handleCustomSegmentCreate = async (segmentName) => {
    if (!selectedProject) return;

    try {
      startLoading('Adding custom technology...');
      await projectsApi.addLeafToProject(selectedProject, {
        customSegment: { name: segmentName }
      });

      showAlert('Custom technology added successfully', 'success');

      // Clear the search box after successful creation
      clearSearch();

      // Refresh project data to get updated segments
      const updatedProject = await projectsApi.getProjectById(selectedProject);
      setProjectData(updatedProject);
    } catch (error) {
      console.error('Failed to add custom technology:', error);
      showAlert('Failed to add custom technology', 'error', error);
    } finally {
      stopLoading();
    }
  };

  const selectedCount = projectData?.leafSegmentProjects?.length || 0;

  useEffect(() => {
    // Reset hover state when chart type changes
    handleMouseOut();
  }, [chartType]);

  const renderContent = () => {
    
    if (loading) {
      return (
        <Content $isPanelsVisible={false} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <LoadingSpinner message="Loading radar data..." />
        </Content>
      );
    }

    if (error) {
      return (
        <Content $isPanelsVisible={false} style={{ padding: '24px' }}>
          <Alert severity="error">
            Error loading radar data: {error}
          </Alert>
        </Content>
      );
    }

    if (!radarData) {
      return (
        <Content $isPanelsVisible={false} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <EmptyState
            title="No Radar Data Available"
            description="Please try again later or contact support if the issue persists."
          />
        </Content>
      );
    }

    // If embedded mode is enabled, render only the chart content
    if (embedded) {
      return renderChartContent();
    }

    return (
      <Content $isPanelsVisible={isPanelsVisible}>
        
        {!hideNavigation && (
          <NavigationPanel
            isExpanded={isNavigationExpanded}
            onToggle={() => setIsNavigationExpanded(!isNavigationExpanded)}
            searchBar={
              <div style={{ flex: 1 }}>
                <SearchBar
                  searchTerm={searchTerm}
                  searchResults={searchResults}
                  showResults={showResults}
                  isGlobalSearch={isGlobalSearch}
                  selectedSubcategory={selectedSubcategory}
                  onSearch={handleSearch}
                  onResultSelect={(result) => {
                    // Get selection details
                    const { selectedNode, pathToNode } = handleResultSelect(result);
                    
                    // First update the expanded path to ensure the node will be visible
                    setExpandedPath(pathToNode);
                    
                    // Add a more reliable sequence of timeouts to ensure:
                    // 1. Path expansion occurs first
                    // 2. Then node selection happens
                    // 3. Then scrolling is triggered when tree has updated
                    
                    // Clear any existing selection first to force a re-render
                    if (handleSelection) {
                      handleSelection(null);
                    }
                    
                    // Use a sequence of setTimeout calls with increasing delays
                    setTimeout(() => {
                      // Now select the node (which will trigger rendering updates)
                      if (handleSelection) {
                        handleSelection(selectedNode);
                      }
                      
                      // The TreeChart component's useEffect for clickedItem changes 
                      // will handle the scrolling after selection
                    }, 300);
                  }}
                  onToggleSearchMode={setIsGlobalSearch}
                  showNoResultsMessage={showNoResultsMessage}
                  width="600px" // Set the width to match the original design
                  allowCustomSegment={configMode === TREE_CHART_MODES.PROJECT}
                  onCustomSegmentCreate={handleCustomSegmentCreate}
                />
              </div>
            }
            breadcrumb={
              <Breadcrumb
                data={filteredData || {}}
                selectedSubcategory={selectedSubcategory}
                onNavigate={handleSelection}
              />
            }
            isVisible={isPanelsVisible}
            onTagSelect={() => setIsTagSelectorOpen(true)}
            selectedTagsCount={selectedTags.length}
            configMode={configMode}
            showOnlySelected={showOnlySelected}
            onToggleShowSelected={() => setShowOnlySelected(!showOnlySelected)}
            chartType={chartType}
            selectedCount={selectedCount}
          />
        )}

        {renderChartContent()}
      </Content>
    );
  };

  // Extract chart content to its own function for reuse
  const renderChartContent = () => {
    return (
      <div className="chart-area" 
        style={{ 
          position: 'relative',
          flex: 1,
          display: 'flex',
          gap: '8px',
          alignItems: 'flex-start',
          minHeight: 0,
          overflow: 'auto',
        }}
        onMouseLeave={handleMouseOut}
      >
        {/* Left Side Panel */}
        {!hideDescription && (
          <div className="side-panel" style={{ 
            width: 'auto',
            position: 'sticky',
            top: 0,
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            backgroundColor: '#fff',
            boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
            transition: 'width 0.3s ease',
            borderRadius: '4px',
          }}>
            <Description data={descriptionData} />
          </div>
        )}

        <ChartPanel>
          <div style={{
            display: 'flex',
            gap: '1rem',
            height: '100%',
          }}>
            <div style={{
              flex: 1,
              position: 'relative',
            }}>
              {chartType === TREE_CHART_MODES.TREE ? (
                <TreeChart
                  data={filteredData}
                  selectedSubcategory={selectedSubcategory}
                  //hoveredItem={hoveredItem}
                  //onHover={handleHover}
                  //onMouseOut={handleMouseOut}
                  projectData={null}
                  hoveredItem={null}
                  onHover={() => {}}
                  onMouseOut={() => {}}
                  onSelection={(node) => {
                    handleSelection(node);
                    if (onNodeSelect) {
                      onNodeSelect(node);
                    }
                  }}
                  onBack={() => {console.log('back')}}
                  zoomFactor={treeZoomFactor}
                  configMode={configMode}
                  selectedProject={selectedProject}
                  expandedPath={expandedPath}
                  setExpandedPath={setExpandedPath}
                  isLeafAssigned={isLeafAssigned}
                  initialCheckedLeafs={initialCheckedLeafs}
                  onLeafToggle={handleLeafToggle}
                  isTreeExpanded={isTreeExpanded}
                  onTreeExpansionToggle={setIsTreeExpanded}
                  onSkillLevelChange={handleSkillLevelChange}
                  tags={tags}
                  tagsLoading={tagsLoading}
                  tagsError={tagsError}
                  onAddNode={async ({ parentNode, ...nodeData }) => {
                    try {
                      startLoading();

                      const newSegment = await radarApi.addSegment({
                        segmentType: nodeData.nodeType,
                        parentSegmentId: parentNode.id,
                        name: nodeData.name,
                        description: nodeData.description,
                        approvalStage: nodeData.approvalStage,
                        tags: nodeData.tags,
                        color: nodeData.color,
                        processes: nodeData.processes,
                        projects: []
                      });

                      setRadarData(prevData => 
                        updateParentNode(prevData, parentNode.id, (parent) => ({
                          ...parent,
                          subcategories: [...(parent.subcategories || []), transformNode(newSegment)]
                        }))
                      );

                      showAlert('Node added successfully', 'success');
                    } catch (error) {
                      showAlert(error.message || 'Operation failed', 'error', error);
                    } finally {
                      stopLoading();
                    }
                  }}
                  onEditNode={async (updatedNode) => {
                    try {
                      startLoading();
                      const updatedData = await radarApi.updateSegment(updatedNode.id, {
                        segmentType: updatedNode.nodeType,
                        name: updatedNode.name,
                        description: updatedNode.description,
                        approvalStage: updatedNode.approvalStage,
                        tags: updatedNode.tags,
                        color: updatedNode.color,
                        processes: updatedNode.processes,
                      });

                      const transformedNode = transformNode(updatedData);

                      setRadarData(prevData =>
                        updateTreeData(prevData, updatedNode.id, (node) => ({
                          ...transformedNode,
                          subcategories: [...(node.subcategories || [])]
                        }))
                      );

                      showAlert('Node updated successfully', 'success');
                      return transformedNode;
                    } catch (error) {
                      showAlert(error.message || 'Operation failed', 'error', error);
                      return null;
                    } finally {
                      stopLoading();
                    }
                  }}
                  onRemoveNode={async (node) => {
                    try {
                      startLoading();

                      // If SafeDeleteDialog already performed the deletion, skip the API call
                      if (!node._alreadyDeleted) {
                        await radarApi.removeSegment(node.id);
                      }

                      // Reset navigation if the deleted node is the current selection
                      // or an ancestor of it (so the breadcrumb doesn't get stuck)
                      if (selectedSubcategory) {
                        const isDeletedNodeSelected = selectedSubcategory.id === node.id;
                        const isSelectedDescendantOfDeleted = node.subcategories &&
                          findDescendantById(node, selectedSubcategory.id);

                        if (isDeletedNodeSelected || isSelectedDescendantOfDeleted) {
                          const parent = getParentSubcategoryData(radarData, node);
                          // Navigate to parent, or root if the parent is the top-level node
                          if (parent && parent.id !== radarData.id) {
                            handleSelection(parent);
                          } else {
                            handleSelection(null);
                          }
                        }
                      }

                      setRadarData(prevData => {
                        const removeNode = (data) => {
                          if (data.subcategories) {
                            return {
                              ...data,
                              subcategories: data.subcategories
                                .filter(child => child.id !== node.id)
                                .map(removeNode)
                            };
                          }
                          return data;
                        };
                        return removeNode(prevData);
                      });

                      showAlert('Node removed successfully', 'success');
                    } catch (error) {
                      showAlert(error.message || 'Operation failed', 'error', error);
                    } finally {
                      stopLoading();
                    }
                  }}
                  onMoveNode={handleNodeMove}
                />
              ) : (
                <RadarChart
                  data={filteredData}
                  selectedSubcategory={selectedSubcategory}
                  zoomFactor={radarZoomFactor}
                  //panPosition={panPosition}
                  isDragging={isDragging}
                  hoveredItem={hoveredItem}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onHover={handleHover}
                  onMouseOut={handleMouseOut}
                  onSelection={(item) => {
                    handleMouseOut();
                    handleSelection(item);
                    if (onNodeSelect) {
                      onNodeSelect(item);
                    }
                  }}
                  onBack={handleBack}
                />
              )}
            </div>

            <div style={{ 
              display: 'flex',
              flexDirection: 'row',
              gap: '1rem',
              alignItems: 'flex-start',
            }}>
              {chartType !== TREE_CHART_MODES.TREE && (
                <div style={{
                  width: '300px',
                }}>
                  <div style={{
                    width: '100%',
                  }}>
                    <Tooltip 
                      data={tooltipData}
                      position={tooltipPosition}
                      approvalStageColors={APPROVAL_STAGE_COLORS}
                      isVisible={!!tooltipData}
                      isFixed={true}
                    />
                  </div>
                </div>
              )}

              <ZoomControls
                zoomFactor={getZoomFactor(chartType)}
                onZoomIn={(isMax) => handleZoomIn(chartType, isMax)}
                onZoomOut={() => handleZoomOut(chartType)}
                onReset={() => handleResetPosition(chartType)}
              />
            </div>
          </div>
        </ChartPanel>
      </div>
    );
  };

  // Handle skill level changes for employee mode
  const handleSkillLevelChange = async (nodeId, level) => {
    try {
      startLoading();
      // TODO: Call your API to save the skill level
      // await employeeApi.saveSkillLevel(email, nodeId, level);
      showAlert('Skill level updated successfully', 'success');
    } catch (error) {
      showAlert('Failed to update skill level', 'error');
    } finally {
      stopLoading();
    }
  };

  const handleNodeMove = async (sourceNode, destinationNode) => {
    try {
      // Call the API to update the node's parent
      const result = await radarApi.moveSegment(sourceNode.id, destinationNode.id);
      showAlert('Node moved successfully', 'success');
      
      // Update the local radar data with the new structure
      const updatedNode = {
        ...result.segment,
        // Preserve the subcategories from the original node if they exist
        subcategories: sourceNode.subcategories || []
      };

      setRadarData(prevData => {
        // First, remove the node from its old parent
        const removeNode = (data) => {
          if (data.subcategories) {
            return {
              ...data,
              subcategories: data.subcategories
                .filter(child => child.id !== sourceNode.id)
                .map(removeNode)
            };
          }
          return data;
        };
        
        // Then, add it to its new parent
        const addToNewParent = (data) => {
          if (data.id === destinationNode.id) {
            return {
              ...data,
              subcategories: [...(data.subcategories || []), updatedNode]
            };
          }
          if (data.subcategories) {
            return {
              ...data,
              subcategories: data.subcategories.map(addToNewParent)
            };
          }
          return data;
        };
        
        const dataWithoutNode = removeNode(prevData);
        return addToNewParent(dataWithoutNode);
      });
      
      return true;
    } catch (error) {
      console.error('Error moving node:', error);
      showAlert('Failed to move node. Please try again.', 'error', error);
      return false;
    }
  };

  // Add useEffect to handle mode changes
  useEffect(() => {
    if (initialConfigMode) {
      setConfigMode(initialConfigMode);
    }
  }, [initialConfigMode]);

  useEffect(() => {
    if (initialChartType) {
      setChartType(initialChartType);
    }
  }, [initialChartType]);

  return (
    <Container>
      {!hideHeader && (
        <Header 
          onBack={() => {
            // If accessed from a project, navigate back to the project edit page
            if (projectId) {
              navigate(`/projects/${projectId}/edit`);
            } else {
              navigate('/');
            }
          }}
          backButtonText={projectId ? "Back to Project" : "Back to Home"}
          showViewSwitch={!!radarData}
          chartType={chartType}
          onViewSwitch={() => setChartType(chartType === TREE_CHART_MODES.TREE 
            ? TREE_CHART_MODES.RADAR 
            : TREE_CHART_MODES.TREE)}
          isPanelsVisible={isPanelsVisible}
          onTogglePanels={handleTogglePanels}
          title={`Technology Radar${ configMode === TREE_CHART_MODES.PROJECT && selectedProject && projectData ? ` | Project | ${projectData.name}` : ''}`}
        />
      )}
      {renderContent()}

      {!hideNavigation && (
        <TagSelectorModal
          isOpen={isTagSelectorOpen}
          onClose={() => setIsTagSelectorOpen(false)}
          tags={tags}
          selectedTags={selectedTags}
          onTagToggle={handleTagToggle}
        />
      )}
    </Container>
  );
};

export default TrendRadarChart;
